import { GeneratePublicKeyRequest, IGeneratePublicKeyResult } from '~src/authentication/serverApi/requests/GeneratePublicKeyRequest';
import { intlSettingsLangChangeTelemetryEvent, intlSettingsLocaleChangeTelemetryEvent } from '~src/commonInfra/telemetryEvents/util/IntlTelemetry';
import { GetShouldSuppressDataOrientationAutoOpenRequest, IGetShouldSuppressDataOrientationAutoOpenParams } from '~src/userSettings/serverApi/requests/dataOrientation/GetShouldSuppressDataOrientationAutoOpenRequest';
import { IUpdateShouldSuppressDataOrientationAutoOpenParams, UpdateShouldSuppressDataOrientationAutoOpenRequest } from '~src/userSettings/serverApi/requests/dataOrientation/UpdateShouldSuppressDataOrientationAutoOpenPane';
import { GetToastAutoDismissSettingRequest, IGetToastAutoDismissSettingParams } from '~src/userSettings/serverApi/requests/GetToastAutoDismissSettingRequest';
import { IUpdateDataManagementAwarenessParams, UpdateDataManagementAwarenessRequest } from '~src/userSettings/serverApi/requests/UpdateDataManagementAwarenessRequest';
import { IUpdateToastAutoDismissParams, UpdateToastAutoDismissRequest } from '~src/userSettings/serverApi/requests/UpdateToastAutoDismissSettingRequest';

import { GetSuspendedDataAlertSettingRequest, IGetSuspendedDataAlertSettingParams } from '../../../dda/serverApi/requests/GetSuspendedDataAlertSettingRequest';
import { IUpdateSuspendedDataAlertSettingParams, UpdateSuspendedDataAlertSettingRequest } from '../../../dda/serverApi/requests/UpdateSuspendedDataAlertSettingRequest';
import { SetShowWelcomeScreenRequest } from '../../../home/requests/SetShowWelcomeScreenRequest';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import WebHttpClient from '../../../httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '../../../httpClient/WebHttpClientWithErrorToast';
import { GetSuspendedSubscriptionSettingRequest, IGetSuspendedSubscriptionSettingParams } from '../../../subscription/serverApi/requests/GetSuspendedSubscriptionSettingRequest';
import { IUpdateSuspendedSubscriptionSettingParams, UpdateSuspendedSubscriptionSettingRequest } from '../../../subscription/serverApi/requests/UpdateSuspendedSubscriptionSettingRequest';
import { ChangeUserPasswordRequest } from '../../serverApi/requests/ChangeUserPasswordRequest';
import { GetMarkAnimationSettingRequest, IGetMarkAnimationSettingParams } from '../../serverApi/requests/GetMarkAnimationSettingRequest';
import { IUpdateMarkAnimationSettingParams, UpdateMarkAnimationSettingRequest } from '../../serverApi/requests/UpdateMarkAnimationSettingRequest';
import { IUpdateNotificationSettingParams, UpdateNotificationSettingRequest } from '../../serverApi/requests/UpdateNotificationSettingRequest';
import { IUpdateSiteStartPageParams, UpdateSiteStartPageRequest } from '../../serverApi/requests/UpdateSiteStartPageRequest';
import { IUpdateUserDisplayNameParams, UpdateUserDisplayNameRequest } from '../../serverApi/requests/UpdateUserDisplayNameRequest';
import { IUpdateUserEmailParams, UpdateUserEmailRequest } from '../../serverApi/requests/UpdateUserEmailRequest';
import { IUpdateUserLanguageParams, UpdateUserLanguageRequest } from '../../serverApi/requests/UpdateUserLanguageRequest';
import { IUpdateUserLocaleParams, UpdateUserLocaleRequest } from '../../serverApi/requests/UpdateUserLocaleRequest';
import { IUpdateUserLocalTimeZoneDataParams, UpdateUserLocalTimeZoneDataRequest } from '../../serverApi/requests/UpdateUserLocalTimeZoneDataRequest';
import { IUpdateUserStartPageParams, UpdateUserStartPageRequest } from '../../serverApi/requests/UpdateUserStartPageRequest';


export namespace UserSettings {
  // Const
  const ResetStartPage = 'unspecified';

  // Note: This is used as sentinel value. It should not be mutated.
  const SameUserId: string = null;

  export function updateUserDisplayName(userId: string, displayName: string): Promise<any> {
    const params: IUpdateUserDisplayNameParams = {
      userId: userId,
      displayName: displayName
    };
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new UpdateUserDisplayNameRequest(params));
  }

  export function updateUserEmail(userId: string, email: string): Promise<any> {
    const params: IUpdateUserEmailParams = {
      userId: userId,
      email: email
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateUserEmailRequest(params));
  }

  export function updateUserEmailAndDelay(userId: string, email: string): Promise<any> {
    const params: IUpdateUserEmailParams = {
      userId: userId,
      email: email
    };
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new UpdateUserEmailRequest(params));
  }

  export function updateUserLanguage(userId: string, language: string): Promise<any> {
    const params: IUpdateUserLanguageParams = {
      userId: userId,
      language: language
    };
    intlSettingsLangChangeTelemetryEvent(language);
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateUserLanguageRequest(params));
  }

  export function updateUserLocale(userId: string, locale: string): Promise<any> {
    const params: IUpdateUserLocaleParams = {
      userId: userId,
      locale: locale
    };
    intlSettingsLocaleChangeTelemetryEvent(locale);
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateUserLocaleRequest(params));
  }

  export function updateUserLocalTimeZoneData(offset: number, timeZoneId: string, isUserSetTimeZone: boolean, userId: string): Promise<any> {
    const params: IUpdateUserLocalTimeZoneDataParams = {
      offset: offset,
      timeZoneId: timeZoneId,
      isUserSetTimeZone: isUserSetTimeZone,
      userId: userId
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateUserLocalTimeZoneDataRequest(params));
  }

  export function updateUserStartPage(userId: string, startPage: string): Promise<any> {
    const params: IUpdateUserStartPageParams = {
      startPage: startPage
    };

    if (userId !== SameUserId) {
      params.userId = userId;
    }
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateUserStartPageRequest(params));
  }

  export function updateCurrentUsersStartPage(startPage: string): Promise<any> {
    const params: IUpdateUserStartPageParams = {
      startPage: startPage
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateUserStartPageRequest(params));
  }

  export function updateSiteStartPage(startPage: string): Promise<any> {
    const params: IUpdateSiteStartPageParams = {
      startPage: startPage
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateSiteStartPageRequest(params));
  }

  export function updateWelcomeChannelEnabled(showWelcomeScreen: boolean): Promise<any> {
    return WebHttpClientWithErrorToastInstance.sendRequest(new SetShowWelcomeScreenRequest({ showWelcomeScreen }));
  }

  export function resetUserStartPage(userId: string): Promise<any> {
    return updateUserStartPage(userId, ResetStartPage);
  }

  export function changeUserPassword(
    userId: string,
    newPassword: string,
    oldPassword?: string): Promise<IResult> {

    return WebHttpClient.sendRequest(new GeneratePublicKeyRequest()).
      then((generatePublicKeyResult: IGeneratePublicKeyResult) => {
        const key = generatePublicKeyResult.key;
        const keyId = generatePublicKeyResult.keyId;

        // server admin does not need to enter old password to change a user's password
        let oldEncryptedPassword: string;
        if (oldPassword && oldPassword.length > 0) {
          oldEncryptedPassword = rsa.encrypt(oldPassword, key);
        }

        const newEncryptedPassword = rsa.encrypt(newPassword, key);

        const changePasswordRequest = new ChangeUserPasswordRequest({
          userId: userId,
          oldEncryptedPassword: oldEncryptedPassword,
          newEncryptedPassword: newEncryptedPassword,
          keyId: keyId
        });
        return WebHttpClientWithErrorToastInstance.sendRequest(changePasswordRequest);
      });
  }

  export function updateNotificationSetting(userId: string, enabled: boolean): Promise<any> {
    const params: IUpdateNotificationSettingParams = {
      userId: userId,
      enabled: enabled
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateNotificationSettingRequest(params));
  }

  export function updateSuspendedSubscriptionSetting(user: string, enabled: boolean): Promise<any> {
    const params: IUpdateSuspendedSubscriptionSettingParams = {
      user,
      enabled
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateSuspendedSubscriptionSettingRequest(params));
  }

  export function getSuspendedSubscriptionSetting(user: string): Promise<any> {
    const params: IGetSuspendedSubscriptionSettingParams = {
      user
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetSuspendedSubscriptionSettingRequest(params));
  }

  export function updateSuspendedDataAlertSetting(user: string, enabled: boolean): Promise<any> {
    const params: IUpdateSuspendedDataAlertSettingParams = {
      user,
      enabled
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateSuspendedDataAlertSettingRequest(params));
  }

  export function getSuspendedDataAlertSetting(user: string): Promise<any> {
    const params: IGetSuspendedDataAlertSettingParams = {
      user
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetSuspendedDataAlertSettingRequest(params));
  }

  export function updateMarkAnimationSetting(user: string, enabled: boolean): Promise<any> {
    const params: IUpdateMarkAnimationSettingParams = {
      user,
      enabled
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateMarkAnimationSettingRequest(params));
  }

  export function getMarkAnimationSetting(user: string): Promise<any> {
    const params: IGetMarkAnimationSettingParams = {
      user
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetMarkAnimationSettingRequest(params));
  }

  export function updateShouldSuppressDataOrientationAutoOpen(user: string, shouldSuppressAutoOpen: boolean): Promise<any> {
    const params: IUpdateShouldSuppressDataOrientationAutoOpenParams = {
      user,
      shouldSuppressAutoOpen
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateShouldSuppressDataOrientationAutoOpenRequest(params));
  }

  export function getShouldSuppressDataOrientationAutoOpen(user: string): Promise<any> {
    const params: IGetShouldSuppressDataOrientationAutoOpenParams = {
      user
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetShouldSuppressDataOrientationAutoOpenRequest(params));
  }

  export function updateDataManagementAwarenessSetting(user: string, prepConductorAwarenessEnabled: boolean, catalogAwarenessEnabled: boolean): Promise<any> {
    let params: IUpdateDataManagementAwarenessParams = {
      user
    };

    if (prepConductorAwarenessEnabled != null) {
      params.prepConductorAwarenessEnabled = prepConductorAwarenessEnabled;
    }

    if (catalogAwarenessEnabled != null) {
      params.catalogAwarenessEnabled = catalogAwarenessEnabled;
    }

    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateDataManagementAwarenessRequest(params));
  }

  export function updateToastAutoDismissSetting(timeout: number): Promise<any> {
    const params: IUpdateToastAutoDismissParams = {
      timeout
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateToastAutoDismissRequest(params));
  }

  export function getToastAutoDismissSetting(): Promise<any> {
    const params: IGetToastAutoDismissSettingParams = {};
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetToastAutoDismissSettingRequest(params));
  }

  export type UserSettings = typeof UserSettings;
}


